// A person's own self-notes (architecture sprint-15 §7.1, §9.4). Every key sits under ['notes', 'mine'].
// Errors on writes are shown inline by the dialog (meta.silent); DELETE answers 204 (apiRequestNoContent).
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { LocalDate } from '@shared/calendar';
import type { NoteShiftDto } from '@shared/shifts';
import { useMe } from '@/hooks/use-me';
import { apiRequest, apiRequestNoContent } from '@/lib/api';
import type { OwnNoteDto, OwnNoteProjectDto } from '@/types/notes';

export const OWN_NOTES_KEY = ['notes', 'mine'] as const;
const PAGE_SIZE = 50;

export interface OwnNotesFilters {
  from: LocalDate;
  to: LocalDate;
  projectId: string | null;
}

interface OwnNotesPage {
  items: OwnNoteDto[];
  nextCursor: string | null;
}

export function useOwnNoteProjects(): UseQueryResult<OwnNoteProjectDto[]> {
  const { data: me } = useMe();
  return useQuery({
    queryKey: [...OWN_NOTES_KEY, 'projects'],
    queryFn: async () => (await apiRequest<{ items: OwnNoteProjectDto[] }>('/api/notes/mine/projects')).items,
    enabled: me?.access.myNotes === true,
  });
}

export function useOwnNotes(filters: OwnNotesFilters): UseInfiniteQueryResult<InfiniteData<OwnNotesPage>> {
  return useInfiniteQuery({
    queryKey: [...OWN_NOTES_KEY, 'list', filters],
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams({ from: filters.from, to: filters.to, limit: String(PAGE_SIZE) });
      if (filters.projectId) q.set('projectId', filters.projectId);
      if (pageParam) q.set('cursor', pageParam);
      return apiRequest<OwnNotesPage>(`/api/notes/mine?${q.toString()}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useOwnShiftOptions(projectId: string | null, date: LocalDate, enabled: boolean): UseQueryResult<NoteShiftDto[]> {
  return useQuery({
    queryKey: [...OWN_NOTES_KEY, 'shift-options', projectId, date],
    queryFn: async () => (await apiRequest<{ items: NoteShiftDto[] }>(`/api/notes/mine/shift-options?projectId=${projectId ?? ''}&date=${date}`)).items,
    enabled: enabled && projectId !== null,
  });
}

export interface OwnNoteInput {
  projectId: string;
  date: LocalDate;
  description: string;
  /** Omitted = auto-link to the open shift today; null = no shift. */
  shiftId?: string | null;
}

function useInvalidateOwnNotes(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: OWN_NOTES_KEY });
}

export function useCreateOwnNote(): UseMutationResult<{ note: OwnNoteDto }, Error, OwnNoteInput> {
  const invalidate = useInvalidateOwnNotes();
  return useMutation({
    mutationFn: (body) => apiRequest('/api/notes/mine', { method: 'POST', body: JSON.stringify(body) }),
    meta: { silent: true },
    onSuccess: invalidate,
  });
}

export function useUpdateOwnNote(): UseMutationResult<{ note: OwnNoteDto }, Error, { noteId: string; patch: Omit<Partial<OwnNoteInput>, 'projectId'> }> {
  const invalidate = useInvalidateOwnNotes();
  return useMutation({
    mutationFn: ({ noteId, patch }) => apiRequest(`/api/notes/mine/${noteId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    meta: { silent: true },
    onSuccess: invalidate,
  });
}

export function useDeleteOwnNote(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateOwnNotes();
  return useMutation({
    mutationFn: (noteId) => apiRequestNoContent(`/api/notes/mine/${noteId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}
